export type SubscriptionRole = "free" | "starter" | "pro";

export type SubscriptionStatus = "active" | "inactive" | "cancelled";

export interface SubscriptionPlan {
  name: string;
  price: number;
  role: SubscriptionRole;
  productId?: string; // DodoPayments product ID
  features: string[];
  available: boolean; // Whether this plan is available for purchase
}

// Product IDs - Test (dev) and Live (prod)
const PRODUCT_IDS_TEST = {
  starter: "pdt_5vBROisII1rsrRH0Gmbyp", // Test starter
  pro: "pdt_IRwlB7JZ3fpWY2Xg59Hm7", // Test pro
};

const PRODUCT_IDS_LIVE = {
  starter: "pdt_L9CwSjAB2n00lp0JM9V6H", // $9/month plan
  pro: "pdt_6mhW2ezEdLpkQPA0irTcH", // $39/month plan
};

// Use live product IDs only in production build
const isProduction = import.meta.env.PROD;
const PRODUCT_IDS = isProduction ? PRODUCT_IDS_LIVE : PRODUCT_IDS_TEST;

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    name: "Free",
    price: 0,
    role: "free",
    features: [
      "3 video exports per month",
      "Basic captions",
      "Standard templates",
      "720p export quality",
      "Watermark on exports",
    ],
    available: false, // Free plan is default, not purchasable
  },
  {
    name: "Starter",
    price: 9,
    role: "starter",
    productId: PRODUCT_IDS.starter,
    features: [
      "30 video exports per month",
      "AI-powered captions",
      "All templates",
      "1080p export quality",
      "No watermark",
    ],
    available: true,
  },
  {
    name: "Pro",
    price: 39,
    role: "pro",
    productId: PRODUCT_IDS.pro,
    features: [
      "Unlimited video exports",
      "AI captions + background removal",
      "Premium templates",
      "4K export quality",
      "Priority support",
    ],
    available: true,
  },
];

export function getPlanByRole(role: SubscriptionRole): SubscriptionPlan | undefined {
  return SUBSCRIPTION_PLANS.find((plan) => plan.role === role);
}

export function getAvailableUpgradePlans(currentRole: SubscriptionRole): SubscriptionPlan[] {
  const roleHierarchy: SubscriptionRole[] = ["free", "starter", "pro"];
  const currentIndex = roleHierarchy.indexOf(currentRole);

  if (currentIndex === -1 || currentIndex === roleHierarchy.length - 1) {
    return []; // Already on highest plan or invalid role
  }

  // Return plans that are higher than current and available
  return SUBSCRIPTION_PLANS.filter(
    (plan) => {
      const planIndex = roleHierarchy.indexOf(plan.role);
      return planIndex > currentIndex && plan.available;
    }
  );
}

export function getFeaturesByRole(role: SubscriptionRole): string[] {
  const plan = getPlanByRole(role);
  return plan?.features || [];
}

